import { Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Payable } from '../../../../models/entities/payable';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Vendor } from '../../../../models/entities/vendors/vendor';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { VendorService } from '../../../../services/billing/vendor.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { EMPTY, forkJoin, Observable } from 'rxjs';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { finalize, map, tap } from 'rxjs/operators';
import { Organization } from '../../../../models/entities/organization';
import { OrganizationService } from '../../../../services/organization/organization.service';
import { VendorEditorDialogComponent, VendorEditorDialogResult } from '../../bill-pay/vendor-editor-dialog/vendor-editor-dialog.component';
import { PayablesService } from '../../../../services/payables/payables.service';
import { PayableStatusBadgeComponent } from '../payables-dashboard/payable-status-badge/payable-status-badge.component';
import { SharedModule } from '../../../shared/shared.module';
import { ExpenseCategoriesFormComponent } from '../../../shared/expense-categories-form/expense-categories-form.component';
import { LegfiCurrencyPipe } from 'app/pipes/format/legfi-currency.pipe';
import { isMoment } from 'moment-timezone';

/**
 * Manages payable (vendor invoice) creation and editing with expense category splitting.
 *
 * Handles vendor invoice data entry with support for expense category allocations. Unlike simple
 * payables, this form enforces exact split matching - when multiple expense categories are used,
 * their sum must precisely equal the invoice amount to prevent accounting discrepancies. Integrates
 * with the payables service's preview endpoint to validate entries before persistence.
 */
/** The API takes a plain YYYY-MM-DD; a loaded payable holds moments, which would serialize as UTC timestamps. */
function toDay(value: any): any {
    return isMoment(value) ? value.format('YYYY-MM-DD') : value;
}

@UntilDestroy()
@Component({
  selector: 'app-payable-detail-form',
  styleUrls: ['./payable-detail-form.component.scss'],
  templateUrl: './payable-detail-form.component.html',
    standalone: true,
    imports: [
        PayableStatusBadgeComponent,
        SharedModule,
        ExpenseCategoriesFormComponent,
        LegfiCurrencyPipe,
        VendorEditorDialogComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PayableDetailFormComponent implements OnInit, OnChanges
{
    @Input() localPayable: Payable | null = null;
    @Input() showActionButtons = true;
    @Input() isProcessing = true;
    @Output() isProcessingChange = new EventEmitter<boolean>();
    @Output() closeRequested = new EventEmitter<void>();
    @Input() preselectedVendorId?: number;
    // SD-7271/7407/7474: the org this payable is being created/edited in. Threaded to the category
    // picker so it loads the payable's org (not the token org) and the save scopes to the same org.
    @Input() organizationId: number = null;

    jwt: JwtLegFiClaims;
    hasWriteAccess = false;
    hasAccrualAccounting = false;
    vendorEditorOpen = false;
    vendorEditorVendorId: number = null;

    invalidControls: string[] = [];
    invalidPaymentAmountMessage: string;

    organization: Organization;
    vendors: Vendor[] = [];
    vendorsLoading = false;
    expenseCategoriesLoading = false;
    payableFormGroup: UntypedFormGroup = this._formBuilder.group({
        id: [
            null,
        ],
        vendorId: [
            null,
            Validators.required,
        ],
        amount: [
            0,
            [
                Validators.required,
                Validators.min(0),
            ],
        ],
        invoiceDate: [
            null,
            Validators.required,
        ],
        dueDate: [
            null,
            Validators.required,
        ],
        memo: [
            null,
        ],
        invoiceNumber: [
            null,
        ],
        transactionDate: [
            null,
        ],
        expenseCategories: this._formBuilder.array([]),
    });

    constructor(
            private _billPayVendorService: VendorService,
            private _formBuilder: UntypedFormBuilder,
            private _growler: GrowlerService,
            private _organizationService: OrganizationService,
            private _payablesService: PayablesService,
    ) {
    }

    get expenseCategories(): UntypedFormArray {
        return this.payableFormGroup.get('expenseCategories') as UntypedFormArray;
    }

    get selectedVendor(): Vendor {
        return this.vendors.find((vendor) => vendor.id === this.payableFormGroup.get('vendorId').value);
    }

    /** One line, or nothing at all: a vendor with no address on file should leave no gap. */
    get selectedVendorAddress(): string {
        const vendor = this.selectedVendor;
        if (!vendor) {
            return '';
        }

        const street = [vendor.address1, vendor.address2].filter(Boolean).join(', ');
        const region = [vendor.city, [vendor.state, vendor.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

        return [street, region].filter(Boolean).join(' · ');
    }

    get vendorItems(): { value: number; label: string }[] {
        return [
            { value: -1, label: 'Add a vendor' },
            ...this.vendors.map((v) => ({
                value: v.id,
                label: v.nickname ? `${v.name} (${v.nickname})` : v.name,
            })),
        ];
    }

    trackByVendorId(_index: number, vendor: Vendor): number {
        return vendor.id;
    }

    get totalAmountInvalid(): boolean {
        return this.payableFormGroup.get('amount').touched && (
                this.payableFormGroup.get('amount').invalid ||
                (this.hasAccrualAccounting && this.invalidControls.includes('splitAmountMismatch'))
        );
    }

    get isLoading(): boolean {
        return this.expenseCategoriesLoading || this.vendorsLoading;
    }

    getTotalAmount(): number {
        return this.payableFormGroup.get('amount')?.value;
    }

    ngOnInit(): void {
        this.jwt = LegFiJwtService.read();
        // SD-7271/7407/7474: default to the token org only when no org was supplied by the dialog.
        this.organizationId = this.organizationId ?? this.jwt.orgId;
        this.hasWriteAccess = LegFiJwtService.doesUserHaveModulePermission(
                'billpay',
                true,
        );

        if (this.localPayable) {
            this.updateForm();
        }
        const joins = [
            this.getOrgPreferences(),
            this.getVendors(),
        ];

        forkJoin(joins).pipe(
            untilDestroyed(this)
        ).subscribe({
            next: () => {
                this.updateForm();

                // Set preselected vendor if provided and no payable exists
                if (this.preselectedVendorId && !this.localPayable) {
                    this.payableFormGroup.patchValue({
                        vendorId: this.preselectedVendorId
                    });
                }

                this.vendorsLoading = false;
                this.isProcessing = false;
            },
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.localPayable && !changes.localPayable.firstChange) {
            this.updateForm();
        }

        if (changes.isProcessing) {
            this.updateFormState();
        }
    }

    updateForm() {
        if (this.localPayable) {
            // Exclude expenseCategories from patchValue since it's managed by the expense-categories-form component
            const { expenseCategories, ...payableDataWithoutExpenseCategories } = this.localPayable;
            this.payableFormGroup.patchValue({
                ...payableDataWithoutExpenseCategories,
                dueDate: toDay(this.localPayable.dueDate),
                invoiceDate: toDay(this.localPayable.invoiceDate),
            });
            this.payableFormGroup.get('transactionDate').setValue(
                    this.localPayable.transactions && this.localPayable.transactions[0]?.transactionDate
                            ? this.localPayable.transactions[0].transactionDate
                            : this.localPayable.paymentSentAt,
            );

            const account = this.localPayable.currentFulfillment?.fulfillmentReference?.account;
            if (account) {
                this.payableFormGroup.get('account').setValue(account);
            }
        }
    }

    updateFormState() {
        if (this.isProcessing) {
            this.payableFormGroup.disable();
        } else {
            this.payableFormGroup.enable();
        }
    }

    previewPayable(): Observable<Payable> {
        if (!this.validateForm()) {
            return EMPTY;
        }
        return this.processPayableSubmission();
    }


    /**
     * Validates form and enforces exact expense category split matching.
     *
     * When expense categories are split (>1 category), validates that the sum of allocations
     * equals the invoice amount using integer arithmetic to avoid floating-point precision errors.
     * This prevents unbalanced accounting entries that could occur from mismatched allocations.
     */
    validateForm() {
        const formGroup = this.payableFormGroup;
        this.invalidControls = [];
        formGroup.markAllAsTouched();

        this.invalidControls = [];

        if (formGroup.invalid) {
            Object.keys(formGroup.controls).forEach(controlName => {
                const control = formGroup.get(controlName);
                if (control && control.invalid) {
                    this.invalidControls.push(controlName);
                }
            });
            this._growler.oops('Please fill out all required invoice detail fields.');
        }

        if (this.expenseCategories.length > 1) {
            const fullAmount = formGroup.get('amount').value;
            const splitSum = this.expenseCategories.controls.reduce((acc, item) =>
                    acc + (item.value['expenseCategoryAmount'] * 100), 0);

            if (splitSum !== (fullAmount * 100)) {
                this._growler.oops(`The split amounts must match the invoice amount exactly.`);
                this.invalidControls.push('splitAmountMismatch');
            }
        }
        return this.invalidControls.length === 0;
    }

    /**
     * Handles inline vendor creation through dropdown's special -1 value.
     *
     * Vendor dropdown uses -1 as a trigger value for "Add New Vendor" option. This pattern
     * allows seamless vendor creation without leaving the payable form, maintaining context
     * during invoice entry workflow.
     */
    protected checkAddVendor(vendorId: number) {
        if (vendorId === -1) {
            this.vendorEditorVendorId = null;
            this.vendorEditorOpen = true;
        }
    }

    protected updateSelectedVendor() {
        this.vendorEditorVendorId = this.selectedVendor.id;
        this.vendorEditorOpen = true;
    }

    protected onVendorEditorClosed(result: VendorEditorDialogResult | null) {
        this.vendorEditorOpen = false;
        if (this.vendorEditorVendorId) {
            if (result?.success) {
                // find this vendor in our list, and update it
                const vendorIndex = this.vendors.findIndex((vendor) => vendor.id === result.vendor.id);
                this.vendors[vendorIndex] = result.vendor;
            }
        } else if (result?.success) {
            this.vendors.push(result.vendor);
            this.payableFormGroup.patchValue({
                vendorId: result.vendor.id,
            });
        } else {
            this.payableFormGroup.patchValue({
                vendorId: null,
            });
        }
    }

    private getOrgPreferences() {
        return this._organizationService
                .getOrganizationById(null, ['preferences'])
                .pipe(tap((organization: Organization) => {
                    this.organization = organization;
                }));
    }

    private getVendors() {
        this.vendorsLoading = true;
        return this._billPayVendorService
                .getOrgVendorList(null, ['lastTransaction', 'vendorBankAccounts'])
                .pipe(
                    map((BillPayVendors: Vendor[]) => {
                        this.vendors = BillPayVendors.filter((vendor: Vendor) => {
                            return !vendor.deletedAt || vendor.lastTransaction;
                        });
                    }),
                );
    }

    private preparePayableData() {
        const dueDate = this.payableFormGroup.get('dueDate').value;
        const invoiceDate = this.payableFormGroup.get('invoiceDate').value;
        return {
            id: this.localPayable?.id,
            amount: this.payableFormGroup.get('amount').value,
            dueDate: toDay(dueDate),
            expenseCategories: this.expenseCategories.value.map(split => {
                return {
                    expenseCategoryAmount: split.expenseCategoryAmount,
                    expenseCategory: {
                        id: split.expenseCategory.id,
                        accountType: split.expenseCategory.accountType,
                    },
                };
            }),
            invoiceDate: toDay(invoiceDate),
            invoiceNumber: this.payableFormGroup.get('invoiceNumber').value,
            memo: this.payableFormGroup.get('memo').value,
            organizationId: this.organizationId,
            vendorId: this.payableFormGroup.get('vendorId').value,
        };
    }

    private processPayableSubmission(): Observable<Payable> {
        this.isProcessing = true;
        this.isProcessingChange.emit(true);

        const obs = this._payablesService.previewPayable(this.preparePayableData());

        return obs.pipe(
            tap({
                error: () => {
                    this._growler.oops('There was a problem saving this payable.');
                }
            }),
            finalize(() => {
                this.isProcessing = false;
            })
        );
    }
}
